/**
 * The data a run_walkthrough tool row carries, and the one-line verdict the card shows
 * for it. Kept out of the component file so the component module exports only
 * components (react-refresh) and the wording is unit-testable on its own.
 */

export interface WalkthroughMatching {
  ideaCount?: number
  anchoredIdeaCount?: number
  matchedCount?: number
  closeCount?: number
  unmatchedIdeaCount?: number
  tradeCount?: number
  tradesInWindow?: number
  unexpectedInWindow?: number
  tradesOutsideWindow?: number
  /** Engine API 2: what the code placed as pending orders and what became of them. */
  pendingOrders?: {
    placed?: number
    filled?: number
    cancelled?: number
    expired?: number
    rejected?: number
    unfilledInWindow?: number
    omittedUnfilled?: number
  }
}

/** The app's verdict on a run — see WalkthroughReport.assess on the server. */
export interface WalkthroughAssessment {
  level?: 'match' | 'close' | 'inconclusive' | 'mismatch' | 'unassessed' | string
  /** 'orders': the code placed pending orders in the drawn period that never filled. */
  cause?: 'history' | 'code' | 'orders' | null | string
}

export interface WalkthroughToolData {
  toolName: string
  toolInput: {
    reason?: string
    minWarmupBars?: number
  }
  startedAt: string
  completedAt?: string
  canceledAt?: string
  status: 'running' | 'completed' | 'canceled' | 'error' | 'timeout'
  toolStatus?: string
  round?: number
  maxRounds?: number
  /** False when the user asked for the run themselves rather than the verify loop. */
  autoRun?: boolean
  btid?: string
  version?: number
  codeVersion?: number
  walkthrough?: {
    symbol?: string
    timeframe?: string
    period?: { startDate?: string; endDate?: string } | null
    /** Trading bars of history the run was given before the first drawn idea. */
    warmupBars?: number | null
    /** The strategy's longest lookback the warm-up was sized from, when known. */
    lookback?: number | null
  }
  matching?: WalkthroughMatching
  assessment?: WalkthroughAssessment
  /** The AI accepted this run as reproducing the drawings. */
  verified?: boolean
  error?: string
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

const LEVEL_LABEL: Record<string, string> = {
  match: 'Match',
  close: 'Close match',
  inconclusive: 'Inconclusive',
  mismatch: 'Differs',
}

/**
 * "Verified · Close match · 2 of 2 trade ideas reproduced · 1 unexpected trade", or the
 * trade count when nothing was marked, or why an inconclusive run could not judge.
 */
export function summaryLine(toolData: WalkthroughToolData): string | null {
  const m = toolData.matching
  const level = toolData.assessment?.level
  const parts: string[] = []
  if (toolData.verified) parts.push('Verified')
  if (level && LEVEL_LABEL[level]) parts.push(LEVEL_LABEL[level])

  if (!m) return parts.length > 0 ? parts.join(' · ') : null

  if (level === 'inconclusive') {
    const lookback = toolData.walkthrough?.lookback
    parts.push(lookback ? `not enough history for a ${lookback}-bar lookback` : 'not enough history to judge')
    return parts.join(' · ')
  }

  if ((m.ideaCount ?? 0) > 0) {
    parts.push(`${m.matchedCount ?? 0} of ${plural(m.ideaCount ?? 0, 'trade idea')} reproduced`)
  } else {
    parts.push(`${plural(m.tradesInWindow ?? 0, 'trade')} in the drawn period`)
  }
  if ((m.unexpectedInWindow ?? 0) > 0) {
    parts.push(plural(m.unexpectedInWindow ?? 0, 'unexpected trade'))
  }
  const placed = m.pendingOrders?.placed ?? 0
  if (placed > 0) {
    const never = Math.max(0, placed - (m.pendingOrders?.filled ?? 0))
    parts.push(never > 0 ? `${plural(placed, 'order')} placed, ${never} never filled` : `${plural(placed, 'order')} placed`)
  }
  return parts.join(' · ')
}
